import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import CssSphere from 'components/ui/CssSphere';
import { getStatusColor } from 'components/Orders/OrderStatus';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ReturnSummary } from 'returnCenter-api';
import { styled } from 'styled-components';
import { getStatusTypeString } from 'utils/helpers';

type ReturnSummaryCellProps = {
    returnSummaries?: ReturnSummary[] | null;
};

const ReturnSummaryCell = ({ returnSummaries }: ReturnSummaryCellProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!returnSummaries || returnSummaries.length === 0) {
        return (
            <SummaryContainer>
                <Hint as="span" fontSize={12}>
                    {t('noReturns', 'No returns')}
                </Hint>
            </SummaryContainer>
        );
    }

    return (
        <SummaryContainer>
            {returnSummaries.map((summary, index) => (
                <SummaryItem
                    key={summary.returnId ?? index}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (summary.returnId && summary.status !== undefined) {
                            const statusKey = getStatusTypeString(summary.status);
                            navigate(`/returns/${statusKey}/${summary.returnId}`, {
                                state: {
                                    returnIdFromPath: summary.returnId,
                                    origin: origin ?? statusKey,
                                    returnStatus: summary.status,
                                },
                            });
                        }
                    }}
                >
                    <CssSphere size={8} color={getStatusColor(summary.status ?? null)} />
                    <ReturnId as="span" bold fontSize={12}>
                        {summary.returnId}
                    </ReturnId>
                    <Quantity as="span" fontSize={11}>
                        ({summary.totalReturnItems ?? 0})
                    </Quantity>
                </SummaryItem>
            ))}
        </SummaryContainer>
    );
};

export default ReturnSummaryCell;

const SummaryContainer = styled(Flex).attrs({ column: true, gap: '0.25rem' })`
    min-width: 120px;
`;

const SummaryItem = styled(Flex).attrs({ gap: '0.35rem' })`
    padding: 0.25rem 0.5rem;
    border-radius: var(--radius);
    background-color: ${({ theme }) => theme.colors.fillLight};
    align-items: center;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.1s ease;

    &:hover {
        background-color: ${({ theme }) => theme.colors.fillHover};
        transform: translateX(2px);
    }
`;

const Hint = styled(Text)`
    color: ${({ theme }) => theme.colors.textFaded};
`;

const ReturnId = styled(Text)`
    color: ${({ theme }) => theme.colors.link};
`;

const Quantity = styled(Text)`
    color: ${({ theme }) => theme.colors.textFaded};
`;
